import React, { useEffect, useMemo, useState } from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import {
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { SERVER_NAME } from './config';
import { DEFAULT_STORY_INTEGRITY_WEIGHTS } from './storyIntegrityUtils';
import { useAuth } from './auth/AuthContext';

const PAPER_DOC_URL = '/e3d_ota_v1.docx';
const PAPER_HTML_URL = '/e3d_ota_v1.html';
const PAPER_PDF_URL = '/e3d_ota_v1.pdf';
const DL_PAPER_HTML_URL = '/e3d_decision_layer_paper_v1.html';
const DL_PAPER_PDF_URL = '/e3d_decision_layer_paper_v1.pdf';
const LIMITATIONS_PULL_QUOTE = 'This paper does not report empirical performance. Any claim of prediction remains a hypothesis until tested with point-in-time data and out-of-sample validation.';

const HIERARCHY_STEPS = [
  { key: 'transactions', label: 'Transactions', subtitle: 'Raw public graph events', href: '/graphs' },
  { key: 'stories', label: 'Stories', subtitle: 'First-order observations', href: '/stories' },
  { key: 'theses', label: 'Theses', subtitle: 'Second-order directional briefs', href: '/thesis' },
  { key: 'actions', label: 'Actions', subtitle: 'Decision support, not execution', href: '/actions' },
  { key: 'outcomes', label: 'Outcomes', subtitle: 'Measured result after each action', href: '/agent-verdicts' },
  { key: 'learning', label: 'Learning', subtitle: 'Weights updated from outcomes', href: '/methodology' },
];

const OTA_EQUATIONS = [
  {
    key: 'story',
    title: 'Story Integrity',
    latex: 'C^{S}_{t} = a_E E_t + a_R R_t + a_I I_t + a_F F_t + a_C C_t',
    target: 'live-admissibility',
    helper: 'Weighted sum of Evidence, Recency, Impact, Frequency, and Convergence signals. Weights satisfy Σa_i = 1.',
  },
  {
    key: 'admissibility',
    title: 'Story Admissibility',
    latex: 'A^{S}_{t} = \\mathbf{1}\\!\\left[C^{S}_{t} \\geq \\tau_S\\right]',
    target: 'live-admissibility',
    helper: 'A Story is admitted when its integrity score clears the family-level threshold τ_S. Inspect live rates in the table below.',
  },
  {
    key: 'overlap',
    title: 'Overlap Vector',
    latex: 'O_t = \\lambda_A O_{\\text{addr}} + \\lambda_K O_{\\text{tok}} + \\lambda_C O_{\\text{contract}} + \\lambda_V O_{\\text{venue}} + \\lambda_{\\tau} O_{\\text{time}} + \\lambda_m O_{\\text{motif}}',
    target: 'live-admissibility',
    helper: 'Convergence across addresses, tokens, contracts, venues, time, and motifs. High overlap increases thesis conviction.',
  },
  {
    key: 'thesis',
    title: 'Thesis Integrity',
    latex: 'C^{\\Theta}_{t} = 0.25\\,Q^{S}_{t} + 0.25\\,O_t + 0.15\\,D_t + 0.15\\,H_t + 0.10\\,X_t - 0.10\\,R^{\\Theta}_{t}',
    target: 'backtest-leaderboard',
    helper: 'Story quality, overlap, direction confidence, hysteresis, cross-asset signal, minus risk. See where the enhanced thesis layer adds measurable signal.',
  },
  {
    key: 'thesis-admissibility',
    title: 'Thesis Admissibility',
    latex: 'A^{\\Theta}_{t} = \\mathbf{1}\\!\\left[C^{\\Theta}_{t} \\geq \\tau_{\\Theta}\\right]',
    target: 'backtest-leaderboard',
    helper: 'A Thesis is admitted — and eligible to generate an Action — when its integrity score clears τ_Θ.',
  },
  {
    key: 'hysteresis',
    title: 'Hysteresis',
    equation: 'cold → warming → active → confirmed',
    target: 'weights-table',
    helper: 'Threshold gaps (τ_up > τ_down) prevent thesis state from flickering around boundaries.',
  },
  {
    key: 'enhanced',
    title: 'Enhanced Thesis',
    latex: 'T^{\\text{enhanced}}_{t} = (1-w_{\\Theta})\\,T^{\\text{base}}_{t} + w_{\\Theta}\\,f_{\\Theta}(\\Theta_t)',
    target: 'backtest-leaderboard',
    helper: 'Base and enhanced delta stay separated so ablations remain inspectable. Δ_Θ measures the uplift.',
  },
  {
    key: 'backtest-model',
    title: 'Backtest Model',
    latex: '\\Pr(Y_{i,t,h}=1) = \\sigma\\!\\bigl(\\beta_0 + \\beta_P X^{P}_{i,t} + \\beta_E X^{\\text{E3D}}_{i,t}\\bigr)',
    target: 'backtest-leaderboard',
    helper: 'Logistic model comparing price-only baseline against price + E3D signals. Δ_E3D is the AUC uplift.',
  },
];

const DL_EQUATIONS = [
  {
    key: 'graph',
    title: 'On-Chain Graph',
    latex: 'G_t = \\bigl(V_t,\\,E_t,\\,M_t\\bigr)',
    target: 'live-admissibility',
    helper: 'V_t: entities (wallets, tokens, contracts). E_t: relationships (transfers, swaps, approvals). M_t: market context. The foundation of every Story.',
  },
  {
    key: 'action-fn',
    title: 'Action Function',
    latex: 'A_t = f_A\\!\\left(\\Theta_t,\\,R_t,\\,C_t,\\,U_t,\\,H_t\\right)',
    target: 'live-admissibility',
    helper: 'A Thesis plus risk, confidence, user context, and expected horizon produces an Action. This is what the Agent Verdict Engine computes.',
  },
  {
    key: 'action-score',
    title: 'Action Score',
    latex: 'Q_A(t) = \\alpha C_t + \\beta I_t + \\gamma K_t - \\delta R_t - \\lambda D_t',
    target: 'live-admissibility',
    helper: 'Conviction, expected impact, confirmation weight, risk penalty, and staleness penalty combine into a single actionability signal.',
  },
  {
    key: 'outcome-fn',
    title: 'Outcome Measurement',
    latex: 'O_{t+h} = f_O\\!\\left(A_t,\\,P_{t+h},\\,L_{t+h},\\,V_{t+h},\\,N_{t+h},\\,D_{t+h}\\right)',
    target: 'live-admissibility',
    helper: 'Outcome is evaluated h periods after the Action against price, liquidity, volume, network, and drawdown. See live verdicts.',
  },
  {
    key: 'outcome-score',
    title: 'Outcome Score',
    latex: 'Q_O(t+h) = \\mu R_P + \\nu R_L + \\xi R_V + \\rho R_N - \\sigma\\,\\text{MDD} - \\kappa X',
    target: 'live-admissibility',
    helper: 'Price return, liquidity, volume, network improvement minus max drawdown and realized risk. Different action types use different weights.',
  },
  {
    key: 'confirmation',
    title: 'Thesis Confirmation',
    latex: '\\Phi_{t+h} = 1 - d\\!\\left(\\widehat{O}_{t+h},\\,O_{t+h}\\right)',
    target: 'live-admissibility',
    helper: 'Distance between expected outcome (implied by the Thesis) and observed outcome. High Φ means the thesis was right.',
  },
  {
    key: 'learning',
    title: 'Learning Loop',
    latex: 'W_{t+1} = W_t + \\eta\\,\\nabla\\mathcal{L}\\!\\left(O_{t+h},\\,\\widehat{O}_{t+h}\\right)',
    target: 'weights-table',
    helper: 'Weights adjust when observed outcomes diverge from expected ones. The rule-weight table below reflects the current W_t.',
  },
];

const EQUATION_CARDS = [...OTA_EQUATIONS, ...DL_EQUATIONS];

const useStyles = makeStyles((theme) => ({
  root: {
    fontFamily: '"IBM Plex Sans", "Inter", sans-serif',
    color: '#ffffff',
    padding: '28px 18px 24px',
    maxWidth: 1180,
    margin: '0 auto',
    minHeight: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    overflowY: 'auto',
    backgroundColor: '#08101f',
    backgroundImage: 'linear-gradient(rgba(158,196,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(158,196,255,0.06) 1px, transparent 1px)',
    backgroundSize: '32px 32px',
    [theme.breakpoints.down('sm')]: {
      padding: '10px 8px 20px',
      backgroundSize: '24px 24px',
    },
  },
  hero: {
    padding: 20,
    borderRadius: 16,
    border: '1px solid rgba(158, 196, 255, 0.24)',
    background: 'linear-gradient(180deg, rgba(8,16,31,0.92), rgba(11,21,38,0.98))',
    boxShadow: '0 0 32px rgba(77,121,255,0.08)',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
    flexWrap: 'wrap',
  },
  title: {
    fontSize: '2.1rem',
    fontWeight: 900,
    letterSpacing: -0.4,
    lineHeight: 1.05,
  },
  subtitle: {
    marginTop: 8,
    color: '#d0dcff',
    lineHeight: 1.6,
    fontSize: 14,
    maxWidth: 820,
  },
  buttonRow: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
  },
  primaryButton: {
    backgroundColor: '#4d79ff',
    color: '#ffffff',
    textTransform: 'none',
    fontWeight: 800,
    borderRadius: 10,
    padding: '8px 12px',
    '&:hover': { backgroundColor: '#3e61cc' },
  },
  ghostButton: {
    color: '#d0dcff',
    textTransform: 'none',
    fontWeight: 700,
    borderRadius: 10,
    padding: '8px 12px',
    border: '1px solid rgba(255,255,255,0.14)',
  },
  hierarchyGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: 10,
    marginTop: 18,
  },
  hierarchyCard: {
    borderRadius: 14,
    border: '1px solid rgba(158, 196, 255, 0.2)',
    background: 'rgba(4, 8, 20, 0.7)',
    padding: 12,
    cursor: 'pointer',
    transition: 'transform 120ms ease, border-color 120ms ease',
    '&:hover': {
      transform: 'translateY(-1px)',
      borderColor: 'rgba(158, 196, 255, 0.42)',
    },
  },
  hierarchyIndex: {
    fontSize: 11,
    color: '#89a8ff',
    fontFamily: '"SFMono-Regular", Consolas, monospace',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  hierarchyLabel: {
    fontSize: 16,
    fontWeight: 900,
    color: '#ffffff',
    marginTop: 4,
  },
  hierarchySubtitle: {
    fontSize: 12,
    lineHeight: 1.55,
    color: '#cfd8ff',
    marginTop: 6,
  },
  arrowLine: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 8,
    color: '#7ea0ff',
    fontFamily: '"SFMono-Regular", Consolas, monospace',
    fontSize: 13,
    letterSpacing: 0.6,
  },
  statGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(185px, 1fr))',
    gap: 10,
  },
  statCard: {
    borderRadius: 14,
    padding: 14,
    border: '1px solid rgba(158, 196, 255, 0.16)',
    background: 'rgba(4, 8, 20, 0.72)',
  },
  statLabel: {
    fontSize: 11,
    color: '#91a4d5',
    textTransform: 'uppercase',
    letterSpacing: 0.55,
    marginBottom: 6,
  },
  statValue: {
    fontSize: 22,
    fontWeight: 900,
    color: '#ffffff',
  },
  statMeta: {
    marginTop: 6,
    fontSize: 11,
    color: '#cfd8ff',
    lineHeight: 1.45,
  },
  section: {
    borderRadius: 16,
    border: '1px solid rgba(158, 196, 255, 0.16)',
    background: 'rgba(9, 15, 29, 0.9)',
    padding: 16,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 900,
    letterSpacing: 0.55,
    textTransform: 'uppercase',
    color: '#e8edff',
    marginBottom: 10,
  },
  body: {
    fontSize: 13,
    color: '#e7efff',
    lineHeight: 1.7,
  },
  equationGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: 10,
  },
  equationCard: {
    borderRadius: 14,
    border: '1px solid rgba(158, 196, 255, 0.18)',
    background: 'rgba(4, 8, 20, 0.72)',
    padding: 14,
    cursor: 'pointer',
  },
  equationTitle: {
    fontSize: 11,
    color: '#91a4d5',
    textTransform: 'uppercase',
    letterSpacing: 0.55,
    marginBottom: 8,
  },
  equationText: {
    fontFamily: '"SFMono-Regular", Consolas, monospace',
    fontSize: 15,
    color: '#a7c5ff',
    lineHeight: 1.45,
  },
  equationHelper: {
    marginTop: 10,
    fontSize: 12,
    color: '#d0dcff',
    lineHeight: 1.55,
  },
  paperGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.15fr) minmax(0, 0.85fr)',
    gap: 12,
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
  callout: {
    padding: 14,
    borderRadius: 14,
    border: '1px solid rgba(158, 196, 255, 0.18)',
    background: 'rgba(4, 8, 20, 0.72)',
  },
  quote: {
    fontSize: 18,
    lineHeight: 1.45,
    color: '#f3f7ff',
    fontStyle: 'italic',
  },
  mono: {
    fontFamily: '"SFMono-Regular", Consolas, monospace',
  },
  tableWrap: {
    overflowX: 'auto',
    borderRadius: 12,
    border: '1px solid rgba(158, 196, 255, 0.12)',
  },
  cellHead: {
    color: '#cfd8ff',
    fontSize: 12,
    fontWeight: 800,
    borderBottom: '1px solid rgba(255,255,255,0.08)',
  },
  cell: {
    color: '#e5e7ff',
    fontSize: 12,
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  },
  familyRateRow: {
    marginTop: 12,
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  familyRateChip: {
    borderRadius: 999,
    border: '1px solid rgba(158, 196, 255, 0.2)',
    background: 'rgba(4, 8, 20, 0.72)',
    padding: '5px 10px',
    fontSize: 11,
    color: '#e5e7ff',
    cursor: 'pointer',
  },
  link: {
    color: '#9ec4ff',
    textDecoration: 'none',
    fontWeight: 700,
  },
}));

function formatPct(value, digits = 1) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 'pending';
  return `${(n * 100).toFixed(digits)}%`;
}

function formatRate(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 'pending';
  return `${n.toFixed(2)} Pr(A=1)`;
}

function formatAucDelta(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 'pending';
  return `${n >= 0 ? '+' : ''}${n.toFixed(3)} AUC`;
}

function formatDeltaNarrative(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 'pending — see protocol §9';
  return `${n >= 0 ? '+' : ''}${n.toFixed(3)} AUC beyond price`;
}

function formatTs(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

function scrollToAnchor(id) {
  try {
    const node = document.getElementById(id);
    if (node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  } catch (e) {
  }
}

function MethodologyLanding({ classes, integrity, backtestSummary, actionsSummary, loadingIntegrity, loadingBacktest, integrityError, backtestError, isAuthenticated }) {
  const navigate = useNavigate();
  const weights = integrity && integrity.default_weights ? integrity.default_weights : DEFAULT_STORY_INTEGRITY_WEIGHTS;
  const metrics = integrity && integrity.metrics ? integrity.metrics : {};
  const families = integrity && Array.isArray(integrity.families) ? integrity.families : [];
  const strongestFamilies = families
    .filter((row) => Number.isFinite(Number(row.admissibility_rate)))
    .sort((a, b) => Number(b.admissibility_rate || 0) - Number(a.admissibility_rate || 0))
    .slice(0, 6);

  return (
    <>
      <div className={classes.hero}>
        <div className={classes.titleRow}>
          <div>
            <div className={classes.title}>OTA v1 Methodology</div>
            <div className={classes.subtitle}>
              Observable Transaction Admissibility is the public measurement layer behind E3D: transactions become stories, stories converge into theses, and theses are judged against live thresholds and outcome protocols.
            </div>
          </div>
          <div className={classes.buttonRow}>
            <Button className={classes.primaryButton} variant="contained" onClick={() => navigate('/methodology/backtest')}>
              Latest Backtests
            </Button>
            <Button className={classes.ghostButton} variant="outlined" onClick={() => navigate('/methodology/weights')}>
              Rule Weights
            </Button>
            <Button className={classes.ghostButton} variant="outlined" component="a" href={PAPER_HTML_URL} target="_blank" rel="noreferrer">
              OTA Paper (HTML)
            </Button>
            <Button className={classes.ghostButton} variant="outlined" component="a" href={PAPER_PDF_URL} target="_blank" rel="noreferrer">
              OTA Paper (PDF)
            </Button>
            <Button className={classes.ghostButton} variant="outlined" component="a" href={DL_PAPER_HTML_URL} target="_blank" rel="noreferrer">
              Decision Layer (HTML)
            </Button>
            <Button className={classes.ghostButton} variant="outlined" component="a" href={DL_PAPER_PDF_URL} target="_blank" rel="noreferrer">
              Decision Layer (PDF)
            </Button>
          </div>
        </div>

        <div className={classes.hierarchyGrid}>
          {HIERARCHY_STEPS.map((step, index) => (
            <div key={step.key} className={classes.hierarchyCard} onClick={() => navigate(step.href)}>
              <div className={classes.hierarchyIndex}>{`Step ${index + 1}`}</div>
              <div className={classes.hierarchyLabel}>{step.label}</div>
              <div className={classes.hierarchySubtitle}>{step.subtitle}</div>
            </div>
          ))}
        </div>
        <div className={classes.arrowLine}>Transactions → Stories → Theses → Actions → Outcomes → Learning</div>
      </div>

      <div className={classes.statGrid}>
        <div className={classes.statCard}>
          <div className={classes.statLabel}>Current Δ_E3D</div>
          <div className={classes.statValue}>{loadingBacktest ? '…' : formatAucDelta(backtestSummary && backtestSummary.current_delta_e3d)}</div>
          <div className={classes.statMeta}>E3D adds {loadingBacktest ? '…' : formatDeltaNarrative(backtestSummary && backtestSummary.current_delta_e3d)} as of today.</div>
        </div>
        <div className={classes.statCard}>
          <div className={classes.statLabel}>Current Δ_Θ</div>
          <div className={classes.statValue}>{loadingBacktest ? '…' : formatAucDelta(backtestSummary && backtestSummary.current_delta_theta)}</div>
          <div className={classes.statMeta}>The convergence layer stays pending when the warehouse has not closed enough outcomes.</div>
        </div>
        <div className={classes.statCard}>
          <div className={classes.statLabel}>Machine-Checkable Invalidations</div>
          <div className={classes.statValue}>{loadingIntegrity ? '…' : Number(metrics.machine_checkable_invalidation_count || 0)}</div>
          <div className={classes.statMeta}>Current theses whose invalidation logic is structured enough for the system to evaluate directly.</div>
        </div>
        <div className={classes.statCard}>
          <div className={classes.statLabel}>Non-Degenerate Admissibility</div>
          <div className={classes.statValue}>{loadingIntegrity ? '…' : formatPct(metrics.non_degenerate_story_pct)}</div>
          <div className={classes.statMeta}>Share of today&apos;s stories that come from families whose admissibility rates remain inside the OTA non-degeneracy band.</div>
        </div>
        <div className={classes.statCard} onClick={() => window.location.assign('/actions')} style={{ cursor: 'pointer' }}>
          <div className={classes.statLabel}>Open Actions (7d)</div>
          <div className={classes.statValue}>
            {actionsSummary
              ? (actionsSummary.breakdown || []).filter((r) => r.status === 'open').reduce((s, r) => s + Number(r.count), 0)
              : '…'}
          </div>
          <div className={classes.statMeta}>Live decision-layer signals derived from thesis conviction, risk pressure, and overlap convergence.</div>
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>Live Counters</div>
        <div className={classes.body}>
          {`Today's Pr(A^S_t = 1) is computed live by family, alongside Δ_E3D, Δ_Θ, invalidation coverage, and admissibility health. Until Phase 4 closes enough outcomes, delta counters stay pending instead of fabricating an edge claim.`}
        </div>
        <div className={classes.familyRateRow}>
          {strongestFamilies.map((row) => (
            <div key={row.family} className={classes.familyRateChip} onClick={() => scrollToAnchor('live-admissibility')}>
              <span className={classes.mono}>{row.family}</span>
              {` · ${formatRate(row.admissibility_rate)}`}
            </div>
          ))}
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>Equation Gallery</div>
        <div style={{ fontSize: 12, color: '#9ea4d8', marginBottom: 14, lineHeight: 1.6 }}>
          The OTA paper defines the measurement layer: how Stories are admitted, how Theses are scored, and how the system proves its edge. The Decision Layer paper extends OTA by adding the Action, Outcome, and Learning layers — turning a measurement framework into a closed-loop intelligence engine.
        </div>
        <div style={{ fontSize: 11, fontWeight: 900, textTransform: 'uppercase', letterSpacing: 0.6, color: '#89a8ff', marginBottom: 8 }}>
          OTA Foundation
        </div>
        <div className={classes.equationGrid} style={{ marginBottom: 20 }}>
          {OTA_EQUATIONS.map((item) => (
            <div key={item.key} className={classes.equationCard} onClick={() => scrollToAnchor(item.target)}>
              <div className={classes.equationTitle}>{item.title}</div>
              {item.latex ? (
                <div
                  style={{ overflowX: 'auto', padding: '4px 0' }}
                  dangerouslySetInnerHTML={{
                    __html: katex.renderToString(item.latex, { displayMode: true, throwOnError: false }),
                  }}
                />
              ) : (
                <div className={classes.equationText}>{item.equation}</div>
              )}
              <div className={classes.equationHelper}>{item.helper}</div>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 11, fontWeight: 900, textTransform: 'uppercase', letterSpacing: 0.6, color: '#a78bfa', marginBottom: 8 }}>
          Decision Layer Extension
        </div>
        <div className={classes.equationGrid}>
          {DL_EQUATIONS.map((item) => (
            <div key={item.key} className={classes.equationCard} onClick={() => scrollToAnchor(item.target)}>
              <div className={classes.equationTitle}>{item.title}</div>
              {item.latex ? (
                <div
                  style={{ overflowX: 'auto', padding: '4px 0' }}
                  dangerouslySetInnerHTML={{
                    __html: katex.renderToString(item.latex, { displayMode: true, throwOnError: false }),
                  }}
                />
              ) : (
                <div className={classes.equationText}>{item.equation}</div>
              )}
              <div className={classes.equationHelper}>{item.helper}</div>
            </div>
          ))}
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>Papers</div>
        <div style={{ fontSize: 13, color: '#e7efff', lineHeight: 1.7, marginBottom: 14 }}>
          E3D is grounded in two public papers. The first defines the measurement framework. The second extends it into a closed-loop decision and learning engine.
        </div>
        <div className={classes.paperGrid}>
          <div className={classes.callout}>
            <div className={classes.equationTitle}>Paper 1 — OTA v1: Observable Transaction Admissibility</div>
            <div className={classes.body}>
              Defines the foundation: how blockchain transactions become Stories, how Stories converge into Theses, and how the system proves it adds signal beyond price alone.
              Introduces Story Integrity (C^S), Thesis Integrity (C^Θ), the Overlap Vector, hysteresis, Δ_E3D, and the backtest protocol.
            </div>
            <div className={classes.body} style={{ marginTop: 10 }}>
              <a href={PAPER_HTML_URL} target="_blank" rel="noreferrer" className={classes.link}>HTML</a>
              {' · '}
              <a href={PAPER_PDF_URL} target="_blank" rel="noreferrer" className={classes.link}>PDF</a>
              {' · '}
              <a href={PAPER_DOC_URL} target="_blank" rel="noreferrer" className={classes.link}>docx</a>
            </div>
            <div className={classes.body} style={{ marginTop: 10 }}>
              <span className={classes.mono}>Default story weights:</span>
              {` a_E ${Number(weights.a_E || 0).toFixed(2)} · a_R ${Number(weights.a_R || 0).toFixed(2)} · a_I ${Number(weights.a_I || 0).toFixed(2)} · a_F ${Number(weights.a_F || 0).toFixed(2)} · a_C ${Number(weights.a_C || 0).toFixed(2)}`}
            </div>
          </div>
          <div className={classes.callout}>
            <div className={classes.equationTitle}>Paper 2 — The E3D Decision Layer</div>
            <div className={classes.body}>
              Built directly on OTA. Where OTA ends at Theses, the Decision Layer adds Actions, Outcomes, and Learning —
              extending the chain to: <span className={classes.mono}>Transactions → Stories → Theses → Actions → Outcomes → Learning</span>.
              Introduces the Action Score (Q_A), Outcome Score (Q_O), Thesis Confirmation (Φ), and weight-update loop.
            </div>
            <div className={classes.body} style={{ marginTop: 10 }}>
              <a href={DL_PAPER_HTML_URL} target="_blank" rel="noreferrer" className={classes.link}>HTML</a>
              {' · '}
              <a href={DL_PAPER_PDF_URL} target="_blank" rel="noreferrer" className={classes.link}>PDF</a>
            </div>
            <div style={{ marginTop: 12 }}>
              <div className={classes.equationTitle}>Decision Layer §1</div>
              <div className={classes.quote}>&ldquo;{LIMITATIONS_PULL_QUOTE}&rdquo;</div>
            </div>
          </div>
        </div>
      </div>

      <div id="backtest-leaderboard" className={classes.section}>
        <div className={classes.sectionTitle}>Latest Backtest Leaderboard</div>
        {loadingBacktest ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" style={{ color: '#cfd3ff' }}>Loading backtest summary…</Typography>
          </div>
        ) : backtestError ? (
          <div className={classes.body}>{backtestError}</div>
        ) : !(backtestSummary && backtestSummary.available) ? (
          <div className={classes.body}>Δ_E3D: pending — see protocol §9.</div>
        ) : (
          <div className={classes.tableWrap}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.cellHead}>Horizon</TableCell>
                  <TableCell className={classes.cellHead}>Liquidity</TableCell>
                  <TableCell className={classes.cellHead}>Family</TableCell>
                  <TableCell className={classes.cellHead}>Δ_E3D</TableCell>
                  <TableCell className={classes.cellHead}>Δ_Θ</TableCell>
                  <TableCell className={classes.cellHead}>CI</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(backtestSummary.leaderboard || []).map((row) => (
                  <TableRow key={`${row.horizon}-${row.liquidity_tier}-${row.story_family}`}>
                    <TableCell className={classes.cell}>{row.horizon}</TableCell>
                    <TableCell className={classes.cell}>{row.liquidity_tier}</TableCell>
                    <TableCell className={classes.cell}>{row.story_family}</TableCell>
                    <TableCell className={classes.cell}>{formatAucDelta(row.delta_e3d)}</TableCell>
                    <TableCell className={classes.cell}>{formatAucDelta(row.delta_theta)}</TableCell>
                    <TableCell className={classes.cell}>
                      {Number.isFinite(Number(row.ci_low)) && Number.isFinite(Number(row.ci_high))
                        ? `${formatPct(row.ci_low)} – ${formatPct(row.ci_high)}`
                        : 'pending'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <div id="live-admissibility" className={classes.section}>
        <div className={classes.sectionTitle}>Live Admissibility</div>
        {loadingIntegrity ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" style={{ color: '#cfd3ff' }}>Loading methodology data…</Typography>
          </div>
        ) : integrityError ? (
          <div className={classes.body}>{integrityError}</div>
        ) : (
          <div className={classes.tableWrap}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.cellHead}>Family</TableCell>
                  <TableCell className={classes.cellHead}>tau_S</TableCell>
                  <TableCell className={classes.cellHead}>Pr(A^S_t = 1)</TableCell>
                  <TableCell className={classes.cellHead}>Stories / 24h</TableCell>
                  <TableCell className={classes.cellHead}>Last Calibrated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {families.map((row) => (
                  <TableRow key={row.family}>
                    <TableCell className={classes.cell}>{row.family}</TableCell>
                    <TableCell className={classes.cell}>{Number(row.tau_s || 0).toFixed(3)}</TableCell>
                    <TableCell className={classes.cell}>{formatPct(row.admissibility_rate)}</TableCell>
                    <TableCell className={classes.cell}>{Number(row.story_count || 0)}</TableCell>
                    <TableCell className={classes.cell}>{formatTs(row.last_calibrated_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <div id="weights-table" className={classes.section}>
        <div className={classes.sectionTitle}>Story Weight Baseline</div>
        <div className={classes.tableWrap}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.cellHead}>a_E</TableCell>
                <TableCell className={classes.cellHead}>a_R</TableCell>
                <TableCell className={classes.cellHead}>a_I</TableCell>
                <TableCell className={classes.cellHead}>a_F</TableCell>
                <TableCell className={classes.cellHead}>a_C</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell className={classes.cell}>{Number(weights.a_E || 0).toFixed(2)}</TableCell>
                <TableCell className={classes.cell}>{Number(weights.a_R || 0).toFixed(2)}</TableCell>
                <TableCell className={classes.cell}>{Number(weights.a_I || 0).toFixed(2)}</TableCell>
                <TableCell className={classes.cell}>{Number(weights.a_F || 0).toFixed(2)}</TableCell>
                <TableCell className={classes.cell}>{Number(weights.a_C || 0).toFixed(2)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>What Is Still Pending</div>
        <div className={classes.body}>
          This page stays conservative. If outcomes are missing, the methodology surface says pending instead of implying predictive performance the paper has not yet earned.
        </div>
        <div className={classes.body} style={{ marginTop: 8 }}>
          Detailed walk-forward tables remain {isAuthenticated ? 'available on the backtest route for your account.' : 'auth-gated on the backtest route while the OTA surface rolls out.'}
        </div>
      </div>
    </>
  );
}

function MethodologyBacktest({ classes, details, loading, error, isAuthenticated }) {
  const navigate = useNavigate();

  return (
    <>
      <div className={classes.hero}>
        <div className={classes.titleRow}>
          <div>
            <div className={classes.title}>Outcome Backtesting</div>
            <div className={classes.subtitle}>
              Walk-forward Δ_E3D and Δ_Θ by horizon, liquidity tier, and story family. The public methodology surface stays visible even when this detailed grid is still gated.
            </div>
          </div>
          <div className={classes.buttonRow}>
            <Button className={classes.ghostButton} variant="outlined" onClick={() => navigate('/methodology')}>
              Methodology
            </Button>
            <Button className={classes.ghostButton} variant="outlined" component="a" href={PAPER_HTML_URL} target="_blank" rel="noreferrer">
              Paper
            </Button>
          </div>
        </div>
      </div>

      {!isAuthenticated ? (
        <div className={classes.section}>
          <div className={classes.sectionTitle}>Authentication Required</div>
          <div className={classes.body}>Sign in to load the detailed backtest grid. The public methodology landing page remains available without authentication.</div>
        </div>
      ) : loading ? (
        <div className={classes.section}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" style={{ color: '#cfd3ff' }}>Loading backtest details…</Typography>
          </div>
        </div>
      ) : error ? (
        <div className={classes.section}>
          <div className={classes.body}>{error}</div>
        </div>
      ) : !(details && details.available) ? (
        <div className={classes.section}>
          <div className={classes.body}>No backtest rows have been written yet.</div>
        </div>
      ) : (
        <>
          <div className={classes.section}>
            <div className={classes.sectionTitle}>Δ_E3D And Δ_Θ</div>
            <div className={classes.tableWrap}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.cellHead}>Horizon</TableCell>
                    <TableCell className={classes.cellHead}>Liquidity</TableCell>
                    <TableCell className={classes.cellHead}>Family</TableCell>
                    <TableCell className={classes.cellHead}>N</TableCell>
                    <TableCell className={classes.cellHead}>Δ_E3D</TableCell>
                    <TableCell className={classes.cellHead}>Δ_Θ</TableCell>
                    <TableCell className={classes.cellHead}>CI</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(details.rows || []).map((row) => (
                    <TableRow key={`${row.horizon}-${row.liquidity_tier}-${row.story_family}`}>
                      <TableCell className={classes.cell}>{row.horizon}</TableCell>
                      <TableCell className={classes.cell}>{row.liquidity_tier}</TableCell>
                      <TableCell className={classes.cell}>{row.story_family}</TableCell>
                      <TableCell className={classes.cell}>{Number(row.n_obs || 0)}</TableCell>
                      <TableCell className={classes.cell}>{formatAucDelta(row.delta_e3d)}</TableCell>
                      <TableCell className={classes.cell}>{formatAucDelta(row.delta_theta)}</TableCell>
                      <TableCell className={classes.cell}>
                        {Number.isFinite(Number(row.ci_low)) && Number.isFinite(Number(row.ci_high))
                          ? `${formatPct(row.ci_low)} – ${formatPct(row.ci_high)}`
                          : 'pending'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          <div className={classes.section}>
            <div className={classes.sectionTitle}>Null Models</div>
            <div className={classes.tableWrap}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.cellHead}>Model</TableCell>
                    <TableCell className={classes.cellHead}>Horizon</TableCell>
                    <TableCell className={classes.cellHead}>Family</TableCell>
                    <TableCell className={classes.cellHead}>AUC</TableCell>
                    <TableCell className={classes.cellHead}>Δ vs random</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(details.null_models || []).map((row, idx) => (
                    <TableRow key={`${row.null_model}-${row.horizon}-${row.story_family}-${idx}`}>
                      <TableCell className={classes.cell}>{row.null_model}</TableCell>
                      <TableCell className={classes.cell}>{row.horizon}</TableCell>
                      <TableCell className={classes.cell}>{row.story_family}</TableCell>
                      <TableCell className={classes.cell}>{Number(row.auc || 0).toFixed(3)}</TableCell>
                      <TableCell className={classes.cell}>{formatAucDelta(row.delta_auc)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </>
      )}
    </>
  );
}

function MethodologyWeights({ classes, weightsData, loading, error, isAdmin, onSave, savingRule }) {
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState({});

  useEffect(() => {
    const nextDrafts = {};
    const rows = weightsData && Array.isArray(weightsData.weights) ? weightsData.weights : [];
    rows.forEach((row) => {
      nextDrafts[row.rule_name] = {
        weight: Number(row.weight || 0).toFixed(4),
        is_pinned: Number(row.is_pinned || 0) ? 1 : 0,
      };
    });
    setDrafts(nextDrafts);
  }, [weightsData]);

  return (
    <>
      <div className={classes.hero}>
        <div className={classes.titleRow}>
          <div>
            <div className={classes.title}>Rule Weight Calibration</div>
            <div className={classes.subtitle}>
              Latest live rule weights, observation counts, and confirmation rates for the candidate engine. The table stays read-only unless the signed-in user has admin override access.
            </div>
          </div>
          <div className={classes.buttonRow}>
            <Button className={classes.ghostButton} variant="outlined" onClick={() => navigate('/methodology')}>
              Methodology
            </Button>
            <Button className={classes.ghostButton} variant="outlined" onClick={() => navigate('/methodology/backtest')}>
              Backtest
            </Button>
            <Button className={classes.ghostButton} variant="outlined" component="a" href={PAPER_HTML_URL} target="_blank" rel="noreferrer">
              Paper
            </Button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className={classes.section}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" style={{ color: '#cfd3ff' }}>Loading rule weights…</Typography>
          </div>
        </div>
      ) : error ? (
        <div className={classes.section}>
          <div className={classes.body}>{error}</div>
        </div>
      ) : (
        <div className={classes.section}>
          <div className={classes.sectionTitle}>Live Rule Weights</div>
          <div className={classes.tableWrap}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.cellHead}>Rule</TableCell>
                  <TableCell className={classes.cellHead}>Version</TableCell>
                  <TableCell className={classes.cellHead}>Weight</TableCell>
                  <TableCell className={classes.cellHead}>Observations</TableCell>
                  <TableCell className={classes.cellHead}>Confirm Rate</TableCell>
                  <TableCell className={classes.cellHead}>Invalidation Rate</TableCell>
                  <TableCell className={classes.cellHead}>Pinned</TableCell>
                  <TableCell className={classes.cellHead}>Last Calibration</TableCell>
                  {isAdmin ? <TableCell className={classes.cellHead}>Admin</TableCell> : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {((weightsData && weightsData.weights) || []).map((row) => {
                  const draft = drafts[row.rule_name] || { weight: Number(row.weight || 0).toFixed(4), is_pinned: Number(row.is_pinned || 0) ? 1 : 0 };
                  return (
                    <TableRow key={row.rule_name}>
                      <TableCell className={classes.cell}>{row.rule_name}</TableCell>
                      <TableCell className={classes.cell}>{Number(row.version || 1)}</TableCell>
                      <TableCell className={classes.cell}>{Number(row.weight || 0).toFixed(4)}</TableCell>
                      <TableCell className={classes.cell}>{Number(row.n_observations || 0)}</TableCell>
                      <TableCell className={classes.cell}>{formatPct(row.confirm_rate)}</TableCell>
                      <TableCell className={classes.cell}>{formatPct(row.invalidation_rate)}</TableCell>
                      <TableCell className={classes.cell}>{Number(row.is_pinned || 0) ? 'Yes' : 'No'}</TableCell>
                      <TableCell className={classes.cell}>{formatTs(row.ts_updated)}</TableCell>
                      {isAdmin ? (
                        <TableCell className={classes.cell}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <TextField
                              value={draft.weight}
                              onChange={(event) => {
                                const value = event.target.value;
                                setDrafts((current) => ({
                                  ...current,
                                  [row.rule_name]: {
                                    ...(current[row.rule_name] || draft),
                                    weight: value,
                                  },
                                }));
                              }}
                              variant="outlined"
                              size="small"
                              style={{ width: 110 }}
                            />
                            <Button
                              className={classes.ghostButton}
                              variant="outlined"
                              onClick={() => {
                                setDrafts((current) => ({
                                  ...current,
                                  [row.rule_name]: {
                                    ...(current[row.rule_name] || draft),
                                    is_pinned: draft.is_pinned ? 0 : 1,
                                  },
                                }));
                              }}
                            >
                              {draft.is_pinned ? 'Unpin' : 'Pin'}
                            </Button>
                            <Button
                              className={classes.primaryButton}
                              variant="contained"
                              disabled={savingRule === row.rule_name}
                              onClick={() => onSave && onSave(row.rule_name, draft)}
                            >
                              {savingRule === row.rule_name ? 'Saving…' : 'Save'}
                            </Button>
                          </div>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </>
  );
}

export default function MethodologyPage() {
  const classes = useStyles();
  const location = useLocation();
  const auth = useAuth();
  const isAuthenticated = !!(auth && auth.isAuthenticated);
  const authRoles = auth && Array.isArray(auth.roles) ? auth.roles : [];
  const isAdmin = authRoles.includes('admin');
  const isWeightsPage = useMemo(() => String(location.pathname || '').startsWith('/methodology/weights'), [location.pathname]);
  const isBacktestPage = useMemo(() => String(location.pathname || '').startsWith('/methodology/backtest'), [location.pathname]);
  const [integrity, setIntegrity] = useState(null);
  const [backtestSummary, setBacktestSummary] = useState(null);
  const [backtestDetails, setBacktestDetails] = useState(null);
  const [weightsData, setWeightsData] = useState(null);
  const [loadingIntegrity, setLoadingIntegrity] = useState(true);
  const [loadingBacktest, setLoadingBacktest] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(isBacktestPage && isAuthenticated);
  const [loadingWeights, setLoadingWeights] = useState(isWeightsPage);
  const [integrityError, setIntegrityError] = useState('');
  const [backtestError, setBacktestError] = useState('');
  const [detailsError, setDetailsError] = useState('');
  const [weightsError, setWeightsError] = useState('');
  const [savingRule, setSavingRule] = useState('');
  const [actionsSummary, setActionsSummary] = useState(null);

  useEffect(() => {
    fetch(`${SERVER_NAME}/actions/summary`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setActionsSummary(d); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoadingIntegrity(true);
        const resp = await fetch(`${SERVER_NAME}/methodology/story-integrity`, { credentials: 'include' });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const json = await resp.json();
        if (active) {
          setIntegrity(json);
          setIntegrityError('');
        }
      } catch (err) {
        if (active) setIntegrityError('Failed to load story integrity methodology.');
      } finally {
        if (active) setLoadingIntegrity(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoadingBacktest(true);
        const resp = await fetch(`${SERVER_NAME}/methodology/backtest-summary`, { credentials: 'include' });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const json = await resp.json();
        if (active) {
          setBacktestSummary(json);
          setBacktestError('');
        }
      } catch (err) {
        if (active) setBacktestError('Failed to load backtest summary.');
      } finally {
        if (active) setLoadingBacktest(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    if (!isWeightsPage) {
      setLoadingWeights(false);
      return () => {
        active = false;
      };
    }

    (async () => {
      try {
        setLoadingWeights(true);
        const resp = await fetch(`${SERVER_NAME}/methodology/rule-weights`, { credentials: 'include' });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const json = await resp.json();
        if (active) {
          setWeightsData(json);
          setWeightsError('');
        }
      } catch (err) {
        if (active) setWeightsError('Failed to load rule weights.');
      } finally {
        if (active) setLoadingWeights(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [isWeightsPage]);

  useEffect(() => {
    let active = true;
    if (!isBacktestPage || !isAuthenticated) {
      setLoadingDetails(false);
      return () => {
        active = false;
      };
    }

    (async () => {
      try {
        setLoadingDetails(true);
        const resp = await fetch(`${SERVER_NAME}/methodology/backtest-details`, { credentials: 'include' });
        if (resp.status === 401) {
          throw new Error('Authentication required.');
        }
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const json = await resp.json();
        if (active) {
          setBacktestDetails(json);
          setDetailsError('');
        }
      } catch (err) {
        if (active) setDetailsError(err && err.message ? err.message : 'Failed to load backtest details.');
      } finally {
        if (active) setLoadingDetails(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [isAuthenticated, isBacktestPage]);

  const handleSaveRule = async (ruleName, draft) => {
    if (!ruleName) return;
    try {
      setSavingRule(ruleName);
      const resp = await fetch(`${SERVER_NAME}/methodology/rule-weights/${encodeURIComponent(ruleName)}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          weight: Number(draft && draft.weight),
          is_pinned: Number(draft && draft.is_pinned) ? 1 : 0,
          update_reason: 'admin_override',
        }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

      const refresh = await fetch(`${SERVER_NAME}/methodology/rule-weights`, { credentials: 'include' });
      if (!refresh.ok) throw new Error(`HTTP ${refresh.status}`);
      const json = await refresh.json();
      setWeightsData(json);
      setWeightsError('');
    } catch (err) {
      setWeightsError(err && err.message ? err.message : 'Failed to save rule weight.');
    } finally {
      setSavingRule('');
    }
  };

  return (
    <div className={classes.root}>
      {isWeightsPage ? (
        <MethodologyWeights
          classes={classes}
          weightsData={weightsData}
          loading={loadingWeights}
          error={weightsError}
          isAdmin={isAdmin || !!(weightsData && weightsData.can_admin_override)}
          onSave={handleSaveRule}
          savingRule={savingRule}
        />
      ) : isBacktestPage ? (
        <MethodologyBacktest
          classes={classes}
          details={backtestDetails}
          loading={loadingDetails}
          error={detailsError}
          isAuthenticated={isAuthenticated}
        />
      ) : (
        <MethodologyLanding
          classes={classes}
          integrity={integrity}
          backtestSummary={backtestSummary}
          actionsSummary={actionsSummary}
          loadingIntegrity={loadingIntegrity}
          loadingBacktest={loadingBacktest}
          integrityError={integrityError}
          backtestError={backtestError}
          isAuthenticated={isAuthenticated}
        />
      )}
    </div>
  );
}
